import { useState, useEffect, useRef, lazy, Suspense } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu, Podcast, Search, LogOut, Settings, UserCircle, LayoutDashboard, Shield, CreditCard } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useAdminPanelLink } from "@/hooks/useAdminPanelLink";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SkipToContent } from "@/components/a11y/SkipToContent";
import { ChevronDown } from "lucide-react";
import { useNavMenu } from "@/hooks/useNavMenu";
import { NavMenuLink } from "@/components/marketing/NavMenuLink";
import { MegaMenuPanel } from "@/components/marketing/MegaMenuPanel";
import { useHeaderMenuStyle } from "@/hooks/useHeaderMenuStyle";
import { BrandLogo } from "@/components/marketing/BrandLogo";
import { useBugReport } from "@/contexts/BugReportContext";


const LazyNotificationCenter = lazy(() => import("@/components/notifications/NotificationCenter").then(m => ({ default: m.NotificationCenter })));
const LazyGlobalSearch = lazy(() => import("@/components/layout/GlobalSearch").then(m => ({ default: m.GlobalSearch })));


interface MarketingHeaderProps {
  hideAuthButtons?: boolean;
  forceOpaque?: boolean;
  /**
   * "onboarding" uses the admin-managed onboarding menu and strips the
   * signed-in app chrome (Dashboard link, search, notifications).
   */
  variant?: "marketing" | "onboarding";
  onLoginClick?: () => void;
  onSignUpClick?: () => void;
}

export function MarketingHeader({ hideAuthButtons = false, forceOpaque = false, variant = "marketing", onLoginClick, onSignUpClick }: MarketingHeaderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile, signOut } = useAuth();
  const { showAdminPanel, adminPanelTo } = useAdminPanelLink();
  const isOnboarding = variant === "onboarding";
  const { menus } = useNavMenu(isOnboarding ? "onboarding" : "header");
  const { launch: launchBugReport } = useBugReport();
  // Always render something clickable: the hook falls back to built-in
  // defaults until the admin-managed menu arrives.
  const headerStyle = useHeaderMenuStyle();
  const { menus: megaMenus } = useNavMenu("header_mega");
  const isMega = !isOnboarding && headerStyle === "mega";
  // Mega columns become heading items so the mobile sheet renders them unchanged.
  // A mega menu without a title holds plain top-level links (e.g. "Why Join PODGO?").
  const navItems = isMega
    ? megaMenus.flatMap((m) =>
        m.title
          ? [{
              ...(m.items[0] ?? {}),
              id: m.id,
              label: m.title,
              link_type: "none" as const,
              href: null,
              children: m.items,
            } as (typeof m.items)[number]]
          : m.items,
      )
    : menus.flatMap((m) => m.items);

  const handleNavAction = (actionKey: string) => {
    if (actionKey === "bug_report") launchBugReport();
    setIsOpen(false);
  };


  const rafId = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      if (rafId.current) return;
      rafId.current = requestAnimationFrame(() => {
        setIsScrolled(window.scrollY > 50);
        rafId.current = 0;
      });
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (rafId.current) cancelAnimationFrame(rafId.current);
    };
  }, []);

  const handleNavClick = (href: string) => {
    if (!href.includes('#')) {
      navigate(href);
      setIsOpen(false);
      return;
    }
    const hash = href.replace('/', '');
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.querySelector(hash);
        if (element) element.scrollIntoView({ behavior: "smooth" });
      }, 300);
    } else {
      const element = document.querySelector(hash);
      if (element) element.scrollIntoView({ behavior: "smooth" });
    }
    setIsOpen(false);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate("/auth");
    } catch (e) {
      const { toast } = await import("@/hooks/use-toast");
      toast({ title: "Sign out failed", description: "Please try again.", variant: "destructive" });
    }
  };

  const getInitials = () => {
    if (profile?.display_name) {
      return profile.display_name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
    }
    if (user?.email) return user.email[0].toUpperCase();
    return "U";
  };

  const getDisplayName = () => {
    return profile?.display_name || user?.email?.split("@")[0] || "User";
  };

  const isOnHomepage = location.pathname === '/';
  const isSignedIn = !!user;
  // Onboarding never shows the marketing "Home" shortcut — the menu is admin-managed.
  const showHomeLink = !isOnHomepage && !isOnboarding;
  const hasNav = navItems.length > 0 || showHomeLink;
  // No links at all: render a logo-only, centered header.
  const centerLogo = isOnboarding && !hasNav;

  return (
    <>
      <SkipToContent />
      <header
        className={`fixed top-[var(--impersonation-h,0px)] left-0 right-0 z-50 border-b transition-all duration-300 ${
          isScrolled || forceOpaque
            ? "bg-primary/95 backdrop-blur-md border-border/20 shadow-lg shadow-black/20"
            : "bg-transparent border-transparent shadow-none"
        }`}
      >
        <div className="container mx-auto px-4 md:px-6">
          <div className={`relative flex h-16 md:h-20 items-center ${centerLogo ? "justify-center" : "justify-between"}`}>

            {/* Logo */}
            <Link to="/" className="flex items-center gap-2" aria-label="PODGO home">
              <BrandLogo surface="dark" className="h-8 md:h-10 w-auto" decorative>
                <>
                  <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg bg-accent flex items-center justify-center">
                    <Podcast className="h-4 w-4 md:h-5 md:w-5 text-accent-foreground" aria-hidden="true" />
                  </div>
                  <span className="text-xl md:text-2xl font-bold text-secondary">PODGO</span>
                </>
              </BrandLogo>
            </Link>

            {/* Desktop Navigation */}
            {hasNav && (
            <div className="hidden md:flex items-center gap-8">
            {showHomeLink && (
              <Link to="/" className="text-secondary/80 hover:text-secondary transition-colors font-medium">
                Home
              </Link>
            )}
            {navItems.map((link) =>
              link.children.length > 0 ? (
                <DropdownMenu key={link.id}>
                  <DropdownMenuTrigger className="text-secondary/80 hover:text-secondary transition-colors font-medium inline-flex items-center gap-1.5">
                    {link.label}
                    <ChevronDown className="h-4 w-4" aria-hidden="true" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" sideOffset={isMega ? 12 : 4} className={isMega ? "w-[min(40rem,90vw)]" : "w-56"}>
                    {isMega ? (
                      <MegaMenuPanel column={link} onAction={handleNavAction} onInternalNavigate={handleNavClick} />
                    ) : link.children.map((child) => (
                      <DropdownMenuItem key={child.id} asChild>
                        <NavMenuLink
                          item={child}
                          className="w-full cursor-pointer"
                          onAction={handleNavAction}
                          onInternalNavigate={handleNavClick}
                        />
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <NavMenuLink
                  key={link.id}
                  item={link}
                  className="text-secondary/80 hover:text-secondary transition-colors font-medium inline-flex items-center gap-1.5"
                  onAction={handleNavAction}
                  onInternalNavigate={handleNavClick}
                />
              )
            )}

          </div>
          )}


          {/* Desktop CTAs / User Actions */}
          {!hideAuthButtons && (
            <div
              className={`hidden md:flex items-center gap-2 ${centerLogo ? "absolute right-0" : ""}`}
            >


              {isSignedIn ? (
                <>
                  {!isOnboarding && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-secondary/80 hover:text-secondary hover:bg-secondary/10"
                        onClick={() => navigate("/dashboard")}
                      >
                        <LayoutDashboard className="h-4 w-4 mr-1.5" aria-hidden="true" />
                        Dashboard
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-secondary/80 hover:text-secondary hover:bg-secondary/10"
                        onClick={() => setSearchOpen(true)}
                        aria-label="Search"
                      >
                        <Search className="h-5 w-5" aria-hidden="true" />
                      </Button>
                      <Suspense fallback={null}>
                        <div className="text-secondary [&_button]:text-secondary/80 [&_button:hover]:text-secondary [&_button:hover]:bg-secondary/10">
                          <LazyNotificationCenter />
                        </div>
                      </Suspense>
                    </>
                  )}

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="relative h-9 w-9 rounded-full hover:bg-secondary/10">
                        <Avatar className="h-9 w-9 ring-2 ring-secondary/20">
                          {profile?.avatar_url && <AvatarImage src={profile.avatar_url} alt={getDisplayName()} />}
                          <AvatarFallback className="bg-accent text-accent-foreground text-sm">
                            {getInitials()}
                          </AvatarFallback>
                        </Avatar>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuLabel>
                        <div className="flex flex-col">
                          <span className="font-medium">{getDisplayName()}</span>
                          <span className="text-xs text-muted-foreground">{user?.email}</span>
                        </div>
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {showAdminPanel ? (
                        <>
                          <DropdownMenuItem onClick={() => navigate(adminPanelTo)}>
                            <Shield className="mr-2 h-4 w-4" aria-hidden="true" />
                            Admin Panel
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate("/dashboard")}>
                            <LayoutDashboard className="mr-2 h-4 w-4" aria-hidden="true" />
                            Podcaster Portal
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                        </>
                      ) : (
                        <DropdownMenuItem onClick={() => navigate("/dashboard")}>
                          <LayoutDashboard className="mr-2 h-4 w-4" aria-hidden="true" />
                          Dashboard
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={() => navigate("/settings")}>

                        <UserCircle className="mr-2 h-4 w-4" aria-hidden="true" />
                        Profile
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/settings")}>
                        <Settings className="mr-2 h-4 w-4" aria-hidden="true" />
                        Account Settings
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/billing")}>
                        <CreditCard className="mr-2 h-4 w-4" aria-hidden="true" />
                        Billing
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleSignOut} className="text-destructive focus:text-destructive">
                        <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
                        Sign out
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    className="text-secondary/80 hover:text-secondary hover:bg-secondary/10"
                    onClick={() => onLoginClick ? onLoginClick() : navigate("/auth?tab=login")}
                  >
                    Log In
                  </Button>
                  <Button
                    className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold"
                    onClick={() => onSignUpClick ? onSignUpClick() : navigate("/auth?tab=signup")}
                  >
                    Get Started
                  </Button>
                </>
              )}
            </div>
          )}

          {/* Mobile Menu — nothing to show when there is no nav and no auth actions */}
          {!(centerLogo && hideAuthButtons) && (
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" className={`text-secondary ${centerLogo ? "absolute right-0" : ""}`} aria-label="Open menu">
                <Menu className="h-6 w-6" aria-hidden="true" />
              </Button>
            </SheetTrigger>

            <SheetContent side="right" className="bg-primary border-border/10 w-[280px] overflow-y-auto overscroll-contain">
              <div className="flex flex-col gap-6 mt-8 pb-[calc(2rem+env(safe-area-inset-bottom))]">
                {/* Signed-in user info in mobile */}
                {isSignedIn && (
                  <div className="flex items-center gap-3 pb-4 border-b border-border/10">
                    <Avatar className="h-10 w-10 ring-2 ring-secondary/20">
                      {profile?.avatar_url && <AvatarImage src={profile.avatar_url} alt={getDisplayName()} />}
                      <AvatarFallback className="bg-accent text-accent-foreground text-sm">
                        {getInitials()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col">
                      <span className="text-secondary font-medium text-sm">{getDisplayName()}</span>
                      <span className="text-secondary/60 text-xs">{user?.email}</span>
                    </div>
                  </div>
                )}

                <div className="flex flex-col gap-4">
                  {showHomeLink && (
                    <Link to="/" onClick={() => setIsOpen(false)} className="text-secondary/80 hover:text-secondary transition-colors font-medium text-left text-lg">
                      Home
                    </Link>
                  )}
                  {isSignedIn && (

                    <button
                      onClick={() => { setIsOpen(false); navigate("/dashboard"); }}
                      className="text-secondary/80 hover:text-secondary transition-colors font-medium text-left text-lg inline-flex items-center gap-2"
                    >
                      <LayoutDashboard className="h-4 w-4" aria-hidden="true" />
                      Dashboard
                    </button>
                  )}
                  {navItems.map((link) => (
                    <div key={link.id} className="flex flex-col gap-3">
                      <NavMenuLink
                        item={link}
                        className="text-secondary/80 hover:text-secondary transition-colors font-medium text-left text-lg inline-flex items-center gap-2"
                        onAction={handleNavAction}
                        onNavigate={() => setIsOpen(false)}
                        onInternalNavigate={handleNavClick}
                      />
                      {link.children.map((child) => (
                        <NavMenuLink
                          key={child.id}
                          item={child}
                          className="pl-4 text-secondary/70 hover:text-secondary transition-colors text-base text-left inline-flex items-center gap-2"
                          onAction={handleNavAction}
                          onNavigate={() => setIsOpen(false)}
                          onInternalNavigate={handleNavClick}
                        />
                      ))}
                    </div>
                  ))}

                </div>

                {!hideAuthButtons && (
                  <div
                    className="flex flex-col gap-3 pt-4 border-t border-border/10"
                  >


                    {isSignedIn ? (
                      <>
                        <Button
                          variant="ghost"
                          className="text-secondary/80 hover:text-secondary hover:bg-secondary/10 justify-start"
                          onClick={() => { setIsOpen(false); navigate("/settings"); }}
                        >
                          <UserCircle className="mr-2 h-4 w-4" aria-hidden="true" />
                          Profile
                        </Button>
                        <Button
                          variant="ghost"
                          className="text-secondary/80 hover:text-secondary hover:bg-secondary/10 justify-start"
                          onClick={() => { setIsOpen(false); navigate("/settings"); }}
                        >
                          <Settings className="mr-2 h-4 w-4" aria-hidden="true" />
                          Account Settings
                        </Button>
                        <Button
                          variant="ghost"
                          className="text-secondary/80 hover:text-secondary hover:bg-secondary/10 justify-start"
                          onClick={() => { setIsOpen(false); navigate("/billing"); }}
                        >
                          <CreditCard className="mr-2 h-4 w-4" aria-hidden="true" />
                          Billing
                        </Button>
                        <Button
                          variant="ghost"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10 justify-start"
                          onClick={async () => {
                            setIsOpen(false);
                            try { await signOut(); } catch {}
                            window.location.href = "/auth";
                          }}
                        >
                          <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
                          Sign out
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant="ghost"
                          className="text-secondary/80 hover:text-secondary hover:bg-secondary/10 justify-start"
                          onClick={() => { setIsOpen(false); onLoginClick ? onLoginClick() : navigate("/auth?tab=login"); }}
                        >
                          Log In
                        </Button>
                        <Button
                          className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold"
                          onClick={() => { setIsOpen(false); onSignUpClick ? onSignUpClick() : navigate("/auth?tab=signup"); }}
                        >
                          Get Started
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </SheetContent>
          </Sheet>
          )}

          {isSignedIn && !isOnboarding && (
            <Suspense fallback={null}>
              <LazyGlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />
            </Suspense>
          )}
        </div>
      </div>
      </header>
    </>
  );
}
